const DB_NAME = "aphelion";
const STORE = "kv";
const ITER = 310000;
const PAD = 65536;

const rand = (n) => {
  const out = new Uint8Array(n);
  for (let i = 0; i < n; i += 65536) crypto.getRandomValues(out.subarray(i, Math.min(n, i + 65536)));
  return out;
};

const idb = () =>
  new Promise((res, rej) => {
    const r = indexedDB.open(DB_NAME, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });

async function tx(mode, fn) {
  const db = await idb();
  return new Promise((res, rej) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    t.oncomplete = () => { db.close(); res(req?.result); };
    t.onerror = () => { db.close(); rej(t.error); };
  });
}

const get = (k) => tx("readonly", (s) => s.get(k));
const put = (k, v) => tx("readwrite", (s) => s.put(v, k));
const del = (k) => tx("readwrite", (s) => s.delete(k));

async function deriveKey(pass, salt) {
  const base = await crypto.subtle.importKey("raw", new TextEncoder().encode(pass), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "PBKDF2", salt, iterations: ITER, hash: "SHA-256" }, base, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

function pad(bytes) {
  const out = rand(Math.ceil((bytes.length + 4) / PAD) * PAD);
  new DataView(out.buffer).setUint32(0, bytes.length);
  out.set(bytes, 4);
  return out;
}

const unpad = (b) => b.slice(4, 4 + new DataView(b.buffer, b.byteOffset).getUint32(0));

async function encrypt(key, bytes) {
  const iv = rand(12);
  return { iv, ct: new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, bytes)) };
}

async function makeSlot(pass, bytes) {
  const salt = rand(16);
  const key = await deriveKey(pass, salt);
  return { key, rec: { salt, ...(await encrypt(key, pad(bytes))) } };
}

export const vaultExists = async () => !!(await get("slot0"));

export async function createVaults(pass, bytes, decoy) {
  const realIdx = rand(1)[0] & 1;
  const real = await makeSlot(pass, bytes);
  const other = decoy ? (await makeSlot(decoy.pass, decoy.bytes)).rec : { salt: rand(16), iv: rand(12), ct: rand(real.rec.ct.length) };
  await put(`slot${realIdx}`, real.rec);
  await put(`slot${1 - realIdx}`, other);
  return { slot: realIdx, key: real.key };
}

export async function writeSlot(idx, pass, bytes) {
  const s = await makeSlot(pass, bytes);
  await put(`slot${idx}`, s.rec);
}

export async function unlock(pass) {
  let found = null;
  for (const i of [0, 1]) {
    const rec = await get(`slot${i}`);
    if (!rec) continue;
    const key = await deriveKey(pass, rec.salt);
    try {
      const pt = new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv: rec.iv }, key, rec.ct));
      if (!found) found = { slot: i, key, bytes: unpad(pt) };
    } catch { /* not this slot */ }
  }
  return found;
}

export async function saveSlot(idx, key, bytes) {
  const rec = await get(`slot${idx}`);
  await put(`slot${idx}`, { salt: rec.salt, ...(await encrypt(key, pad(bytes))) });
}

export async function putFile(key, id, bytes) {
  await put(`f:${id}`, await encrypt(key, bytes));
}

export async function getFile(key, id) {
  const rec = await get(`f:${id}`);
  if (!rec) return null;
  try {
    return new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv: rec.iv }, key, rec.ct));
  } catch {
    return null;
  }
}

export const delFile = (id) => del(`f:${id}`);

const b64u = (u) => {
  let s = "";
  for (let i = 0; i < u.length; i += 0x8000) s += String.fromCharCode.apply(null, u.subarray(i, i + 0x8000));
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};
const ub64u = (s) => Uint8Array.from(atob(s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4)), (c) => c.charCodeAt(0));
const canZip = () => typeof CompressionStream !== "undefined" && typeof DecompressionStream !== "undefined";
const pipe = async (bytes, S) => new Uint8Array(await new Response(new Blob([bytes]).stream().pipeThrough(new S("deflate-raw"))).arrayBuffer());

export async function sealExport(pass, bytes) {
  const zip = canZip();
  const body = zip ? await pipe(bytes, CompressionStream) : bytes;
  const pt = new Uint8Array(body.length + 1);
  pt[0] = zip ? 1 : 0;
  pt.set(body, 1);
  const salt = rand(16);
  const { iv, ct } = await encrypt(await deriveKey(pass, salt), pt);
  const out = new Uint8Array(28 + ct.length);
  out.set(salt);
  out.set(iv, 16);
  out.set(ct, 28);
  return "aphk1." + b64u(out);
}

export async function openExport(pass, str) {
  const s = (str || "").replace(/\s+/g, "");
  if (!s.startsWith("aphk1.")) return null;
  try {
    const raw = ub64u(s.slice(6));
    const key = await deriveKey(pass, raw.slice(0, 16));
    const pt = new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv: raw.slice(16, 28) }, key, raw.slice(28)));
    return pt[0] === 1 ? await pipe(pt.slice(1), DecompressionStream) : pt.slice(1);
  } catch {
    return null;
  }
}

export const wipeAll = () =>
  new Promise((res) => {
    const r = indexedDB.deleteDatabase(DB_NAME);
    r.onsuccess = r.onerror = r.onblocked = () => res();
  });
